import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import ActionButton from "../../components/query/button";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import {
  getConflictCaseById,
  updateConflictCase,
  addConflictNote,
  listConflictParticipants,
} from "../../api/conflicts";

const statusOptions = [
  "Reported",
  "Under Review",
  "Mediation Scheduled",
  "Action Required",
  "Resolved",
  "Closed",
];

const priorityOptions = ["Low", "Medium", "High", "Critical"];

const StatusPill = ({ status }) => {
  const classes = {
    Reported: "bg-[#F1F5F9] text-[#0F172A]",
    "Under Review": "bg-[#F8FAFC] text-[#0F172A] border border-[#94A3B8]",
    "Mediation Scheduled": "bg-[#E0F2FE] text-[#0369A1]",
    "Action Required": "bg-[#FEF3C7] text-[#B45309]",
    Resolved: "bg-[#DCFCE7] text-[#166534]",
    Closed: "bg-[#FEE2E2] text-[#991B1B]",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${classes[status] || classes.Reported}`}>
      {status}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ConflictDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyAccount, currentEmployee } = useCurrentEmployee();
  const [conflict, setConflict] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [updatePayload, setUpdatePayload] = useState({
    status: "",
    priority: "Medium",
    mediatorId: "",
    resolutionSummary: "",
    dueDate: "",
  });

  const positionName = `${currentEmployee?.positionName || currentEmployee?.position?.name || ""}`.toLowerCase();
  const isAdmin = companyAccount || currentEmployee?.modulePermissions?.includes("conflict-resolution");
  const isHod = !isAdmin && (positionName.includes("head") || positionName.includes("hod") || !!currentEmployee?.isHeadOfDepartment);
  const canManageConflict = isAdmin || isHod;

  const loadParticipants = async () => {
    try {
      const response = await listConflictParticipants();
      setParticipants(response.participants || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load participants.");
    }
  };

  const loadConflict = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getConflictCaseById(id);
      const fetched = response?.conflict || null;
      setConflict(fetched);
      setUpdatePayload({
        status: fetched?.status || "",
        priority: fetched?.priority || "Medium",
        mediatorId: fetched?.mediator?.id || "",
        resolutionSummary: fetched?.resolutionSummary || "",
        dueDate: fetched?.dueDate ? fetched.dueDate.split("T")[0] : "",
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load conflict details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConflict();
    loadParticipants();
  }, [id]);

  const handleUpdate = async () => {
    if (!conflict?.id) return;
    setSaving(true);
    try {
      await updateConflictCase(conflict.id, {
        status: updatePayload.status,
        priority: updatePayload.priority,
        mediatorId: updatePayload.mediatorId || null,
        resolutionSummary: updatePayload.resolutionSummary,
        dueDate: updatePayload.dueDate || null,
      });
      toast.success("Conflict updated successfully.");
      loadConflict();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update conflict.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!conflict?.id) return;
    if (!noteBody.trim()) {
      toast.error("Enter a note before saving.");
      return;
    }

    setSaving(true);
    try {
      await addConflictNote(conflict.id, { body: noteBody.trim() });
      toast.success("Note added successfully.");
      setNoteBody("");
      loadConflict();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to add note.");
    } finally {
      setSaving(false);
    }
  };

  const participantOptions = useMemo(
    () =>
      participants.map((employee) => ({
        id: employee.id,
        label: `${employee.firstName || ""} ${employee.lastName || ""}`.trim(),
      })),
    [participants]
  );

  return (
    <div className="min-h-screen p-6">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F172A]">Conflict action page</h1>
          <p className="mt-2 text-sm text-[#475569]">
            Manage the reported conflict on a dedicated page and keep the landing page available for new cases.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/conflict-resolution")}
          className="rounded-xl border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#F8FAFC]"
        >
          Back to conflict list
        </button>
      </div>

      {loading ? (
        <FetchLoadingState message="Loading conflict details..." className="min-h-[360px] rounded-3xl border border-[#E2E8F0] bg-white shadow-sm" />
      ) : !conflict ? (
        <div className="rounded-3xl border border-[#E2E8F0] bg-white p-10 text-center text-sm text-[#B42318] shadow-sm">
          Conflict not found.
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.4fr_0.85fr]">
          <div className="rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <div className="rounded-2xl bg-[#F8FAFC] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-[#475569]">{conflict.category}</p>
                  <h2 className="mt-2 text-2xl font-semibold text-[#0F172A]">{conflict.title}</h2>
                </div>
                <div className="space-y-2 text-right">
                  <StatusPill status={conflict.status} />
                  <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">{conflict.priority}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-3 text-sm text-[#475569]">
              <div className="rounded-2xl bg-[#F8FAFC] p-4 flex items-center justify-between">
                <span>Reporter</span>
                <span>{conflict.reporter?.name || "Unknown"}</span>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4 flex items-center justify-between">
                <span>Respondent</span>
                <span>{conflict.respondent?.name || "Unassigned"}</span>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4 flex items-center justify-between">
                <span>Mediator</span>
                <span>{conflict.mediator?.name || "Not assigned"}</span>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4 flex items-center justify-between">
                <span>Due date</span>
                <span>{formatDate(conflict.dueDate)}</span>
              </div>
            </div>

            <div className="mt-6 space-y-5 text-sm text-[#475569]">
              <div>
                <p className="font-semibold text-[#0F172A]">Description</p>
                <p className="mt-2 whitespace-pre-line">{conflict.description}</p>
              </div>
              {conflict.desiredOutcome && (
                <div>
                  <p className="font-semibold text-[#0F172A]">Desired outcome</p>
                  <p className="mt-2 whitespace-pre-line">{conflict.desiredOutcome}</p>
                </div>
              )}
              {conflict.resolutionSummary && (
                <div>
                  <p className="font-semibold text-[#0F172A]">Resolution notes</p>
                  <p className="mt-2 whitespace-pre-line">{conflict.resolutionSummary}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
              <p className="text-base font-semibold text-[#0F172A]">Action</p>
              {canManageConflict ? (
                <div className="mt-4 space-y-4">
                  <div className="space-y-3">
                    <label className="block text-sm text-[#475569]">Status</label>
                    <select
                      value={updatePayload.status}
                      onChange={(event) => setUpdatePayload((prev) => ({ ...prev, status: event.target.value }))}
                      className="w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-2 text-sm text-[#0F172A]"
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm text-[#475569]">Priority</label>
                    <select
                      value={updatePayload.priority}
                      onChange={(event) => setUpdatePayload((prev) => ({ ...prev, priority: event.target.value }))}
                      className="w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-2 text-sm text-[#0F172A]"
                    >
                      {priorityOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm text-[#475569]">Mediator</label>
                    <select
                      value={updatePayload.mediatorId}
                      onChange={(event) => setUpdatePayload((prev) => ({ ...prev, mediatorId: event.target.value }))}
                      className="w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-2 text-sm text-[#0F172A]"
                    >
                      <option value="">Not assigned</option>
                      {participantOptions.map((participant) => (
                        <option key={participant.id} value={participant.id}>{participant.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm text-[#475569]">Resolution summary</label>
                    <textarea
                      rows={4}
                      value={updatePayload.resolutionSummary}
                      onChange={(event) => setUpdatePayload((prev) => ({ ...prev, resolutionSummary: event.target.value }))}
                      className="w-full resize-none rounded-xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm text-[#475569]">Due date</label>
                    <input
                      type="date"
                      className="w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-2 text-sm text-[#0F172A]"
                      value={updatePayload.dueDate}
                      onChange={(event) => setUpdatePayload((prev) => ({ ...prev, dueDate: event.target.value }))}
                    />
                  </div>
                  <ActionButton
                    label={saving ? "Saving..." : "Save update"}
                    onClick={handleUpdate}
                    className="w-full"
                  />
                </div>
              ) : (
                <div className="mt-4 rounded-2xl bg-[#F8FAFC] p-4 text-sm text-[#475569]">
                  Only administrators and department heads can update conflict status, priority, or assignment. Use the note area below to record observations or follow-up actions.
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
              <p className="text-base font-semibold text-[#0F172A]">Add note</p>
              <textarea
                rows={4}
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
                placeholder="Document progress, meeting notes, or next steps."
                className="mt-4 w-full resize-none rounded-xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-3 text-sm text-[#0F172A]"
              />
              <ActionButton
                label={saving ? "Saving note..." : "Save note"}
                onClick={handleAddNote}
                className="mt-4 w-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictDetailPage;
